import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowDown, ArrowUp, ArrowUpDown, Download, ExternalLink, FileSpreadsheet, Link2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { DeplacerOperation, beneficiaire, cleGroupe } from "@/components/DeplacerOperation";
import { DoublonBouton } from "@/components/DoublonLien";
import { CommentaireOperation } from "@/components/CommentaireOperation";
import { estAssurance, taxePrime, TAUX_TAXE_PRIME } from "@/lib/assurance";
import { errMsg, money, telechargerExcel, telechargerPost } from "@/lib/api";

const AIDE_AUTRES = {
  rev: "Dépôts Desjardins qui ne sont ni Airbnb/Booking, ni associés à un locataire, ni reconnus par vos types de revenus. Associez-les à un locataire depuis le Rapprochement, ou ajoutez des mots-clés dans Paramètres › Types de revenus.",
  dep: "Dépenses Desjardins dont la description ne correspond à aucun mot-clé de vos catégories. Classez-les dans Commentaires opérations, ou ajoutez des mots-clés dans Paramètres › Catégories.",
};

const BoutonPdf = ({ titre, periode, revenus, items }) => {
  const [envoi, setEnvoi] = useState(false);
  const telecharger = async () => {
    setEnvoi(true);
    try {
      await telechargerPost("/dashboard/detail-pdf", { titre, periode, revenus, lignes: items.map(({ date, description, montant, total, commentaire }) => ({ date, description, montant, total: total ?? null, commentaire: commentaire || null })) }, {}, `Détail ${titre}.pdf`);
    } catch (e) { toast.error(errMsg(e)); } finally { setEnvoi(false); }
  };
  const excel = async () => {
    setEnvoi(true);
    const lignes = items.map((o) => [{ v: o.date }, { v: o.description }, { v: o.commentaire || "" }, { v: o.montant, f: "money" }, { v: o.total ?? null, f: "money" }]);
    try { await telechargerExcel(`Détail ${titre}`.slice(0, 80), { colonnes: ["Date", "Description", "Commentaire", "Montant", "Total réparti"], lignes }); }
    catch (e) { toast.error(errMsg(e)); } finally { setEnvoi(false); }
  };
  return (
    <div className="flex gap-2">
      <Button size="sm" variant="outline" onClick={telecharger} disabled={envoi || !items.length} data-testid="categorie-detail-pdf">
        {envoi ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Download className="mr-2 h-4 w-4" />}Imprimer (PDF)
      </Button>
      <Button size="sm" variant="outline" onClick={excel} disabled={envoi || !items.length} data-testid="categorie-detail-excel">
        <FileSpreadsheet className="mr-2 h-4 w-4" />Excel
      </Button>
    </div>
  );
};

const COLONNES = [["date", "Date", ""], ["description", "Description", ""], ["montant", "Montant", "text-right"]];

export const infoParts = (parts) => `Opération répartie : ${parts.map((p) => `${p.immeuble} ${money(p.montant)}`).join(" · ")}`;

// Montant d'une part d'opération répartie : chaîne + « sur » le montant d'origine
const MontantPart = ({ t, testId }) => (
  <span className="inline-flex flex-col items-end leading-tight" title={infoParts(t.parts)} data-testid={testId}>
    <span className="inline-flex items-center gap-1"><Link2 className="h-3.5 w-3.5 text-sky-600" />{money(t.montant)}</span>
    <span className="text-[10px] text-slate-400">sur {money(t.total)}</span>
  </span>
);

const trier = (items, tri) => {
  if (!tri) return items;
  const { col, asc } = tri;
  const cmp = (a, b) => (col === "montant" ? a.montant - b.montant : String(a[col] || "").localeCompare(String(b[col] || ""), "fr", { sensitivity: "base" }));
  return [...items].sort((a, b) => (asc ? cmp(a, b) : cmp(b, a)));
};

const EnTete = ({ tri, setTri, deplacer, doublon }) => (
  <thead><tr className="text-left text-[11px] font-mono uppercase tracking-wider text-slate-500">
    {COLONNES.map(([col, lib, cls]) => {
      const actif = tri?.col === col;
      const Icone = !actif ? ArrowUpDown : tri.asc ? ArrowUp : ArrowDown;
      return (
        <th key={col} className={`pb-1 ${cls}`}>
          <button type="button" onClick={() => setTri({ col, asc: !actif || !tri.asc })} data-testid={`detail-tri-${col}`}
            className={`inline-flex items-center gap-1 uppercase transition-colors hover:text-slate-900 ${actif ? "text-slate-900" : ""}`}>
            {lib}<Icone className={`h-3 w-3 ${actif ? "" : "opacity-40"}`} />
          </button>
        </th>
      );
    })}
    {deplacer && <th className="pb-1 pl-2 whitespace-nowrap">Déplacer vers</th>}
    {doublon && <th className="pb-1 pl-2 whitespace-nowrap">Doublon</th>}
  </tr></thead>
);

// Détail des opérations d'une catégorie de dépense ou de revenu du tableau de bord
const memesIds = (items, t) => items.filter((x) => cleGroupe(beneficiaire(x.description)) === cleGroupe(beneficiaire(t.description))).map((x) => x.id);

export const AutresDepensesDialog = ({ open, items: brut, onClose, revenus = false, categorie, autre = false, periode, cl = null, recharger = null }) => {
  const [tri, setTri] = useState(null);
  const [deplaces, setDeplaces] = useState([]);
  const [notes, setNotes] = useState({});
  useEffect(() => { setTri(null); setDeplaces([]); setNotes({}); }, [categorie, open]);
  const items = trier(brut.filter((t) => !deplaces.includes(t.id)).map((t) => (t.id in notes ? { ...t, commentaire: notes[t.id] } : t)), tri);
  const deplacer = !!cl;
  const doublon = !!recharger;
  const assurance = !revenus && estAssurance(categorie);
  const onDoublon = (ids) => { if (ids) { setDeplaces((d) => [...d, ...ids]); } recharger(); };
  return (
  <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
    <DialogContent onInteractOutside={(e) => { if (e.target?.closest?.("[data-sonner-toaster]")) { e.preventDefault(); } }}
      onEscapeKeyDown={(e) => { if (document.activeElement?.dataset?.testid?.startsWith("detail-commentaire-")) { e.preventDefault(); } }}
      className={`${deplacer || doublon ? "max-w-5xl" : "max-w-2xl"} max-h-[85vh] overflow-y-auto`} data-testid={autre ? (revenus ? "autres-revenus-dialog" : "autres-dialog") : "categorie-detail-dialog"}>
      <DialogHeader>
        <DialogTitle data-testid="categorie-detail-titre">{revenus ? "Revenus" : "Dépenses"} « {categorie} » · {money(items.reduce((s, t) => s + t.montant, 0))}</DialogTitle>
        <DialogDescription>{autre ? AIDE_AUTRES[revenus ? "rev" : "dep"] : `${items.length} opération(s) Desjardins · ${periode || "Toutes les périodes"}`}</DialogDescription>
        {assurance && <p className="text-xs text-slate-600" data-testid="categorie-detail-taxe-prime">Dont taxe sur prime d'assurance ({TAUX_TAXE_PRIME} %, non récupérable, comprise dans le coût) : {money(taxePrime(items.reduce((s, t) => s + t.montant, 0)))}</p>}
      </DialogHeader>
      <div><BoutonPdf titre={categorie} periode={periode} revenus={revenus} items={items} /></div>
      <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <EnTete tri={tri} setTri={setTri} deplacer={deplacer} doublon={doublon} />
        <tbody>{items.map((t, i) => (
          <tr key={t.id || i} className="border-t border-slate-100" data-testid={`${revenus ? "autre-revenu" : "autre-depense"}-${i}`}>
            <td className="py-1.5 pr-2 whitespace-nowrap font-mono text-xs">{t.date}
              {t.id && (
                <a href={`/commentaires?mois=${t.date.slice(0, 7)}&op=${t.id}&retour=${encodeURIComponent(`${revenus ? "Revenus" : "Dépenses"} « ${categorie} »`)}`} target="_blank" rel="noopener noreferrer" title="Voir l'opération dans Commentaires opérations (nouvel onglet)"
                  className="mt-0.5 flex items-center gap-1 font-sans text-[11px] text-emerald-700 hover:underline" data-testid={`detail-voir-${i}`}><ExternalLink className="h-3 w-3" />Voir</a>
              )}
            </td>
            <td className="py-1.5 pr-2 text-xs text-slate-700">{t.description}
              {t.id && <CommentaireOperation id={t.id} valeur={t.commentaire || ""} testId={`detail-commentaire-${i}`}
                onSaved={(id, v) => { setNotes((n) => ({ ...n, [id]: v })); if (recharger) { recharger(); } }} />}
            </td>
            <td className="py-1.5 text-right tabular whitespace-nowrap">{t.partiel ? <MontantPart t={t} testId={`detail-part-${i}`} /> : money(t.montant)}
              {assurance && <span className="block text-[10px] text-slate-400" data-testid={`detail-taxe-prime-${i}`}>dont taxe {money(taxePrime(t.montant))}</span>}
            </td>
            {deplacer && (
              <td className="py-1.5 pl-2">
                {t.id ? <DeplacerOperation t={t} memes={memesIds(items, t)} cl={cl} actuelle={categorie} onDeplace={(ids) => setDeplaces((d) => [...d, ...ids])}
                  onAnnule={(ids) => setDeplaces((d) => d.filter((x) => !ids.includes(x)))} testId={`deplacer-${i}`} /> : null}
              </td>
            )}
            {doublon && (
              <td className="py-1.5 pl-2">
                {t.id && !t.partiel ? <DoublonBouton op={{ id: t.id, date: t.date, description: t.description, [revenus ? "credit" : "debit"]: t.montant }} onChange={onDoublon} testId={`doublon-${i}`} /> : null}
              </td>
            )}
          </tr>
        ))}</tbody>
      </table>
      </div>
      {!revenus && autre && <Link to="/commentaires" className="text-sm font-medium text-emerald-700 hover:underline" data-testid="autres-classer-link">Classer ces dépenses dans Commentaires opérations →</Link>}
    </DialogContent>
  </Dialog>
  );
};
